import { DeliveryListGroupingValues } from 'src/entities/licenseEntity';
import { ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';

/** legRun の積地側 / 降地側 */
export type PlaceSide = 'loading' | 'unloading';

/**
 * 地点キーの導出。
 *
 * Why 1 箇所に集める: 同じ導出がサーバのデータからカードを作る経路 (vehicleRowVM) と
 * D&D 直後の楽観更新 (Presenter の placeLegRunInRow) の 2 箇所にある。片方だけ
 * 「配送リストの地点表示」設定に追随させると、ドロップ直後だけカードが 2 枚に割れて
 * 再取得で 1 枚に戻る、という直しにくい挙動になる。
 */
const rawName = (side: PlaceSide, legRun: ShipmentOrderLegRunForPlanEntity): string =>
  (side === 'loading' ? legRun.loading_name : legRun.unloading_name) ?? '';

/** カードの見出しに出す地点名。legRun が引けない operation 専用カードでは '—' */
export function placeNameOf(
  side: PlaceSide,
  legRun: ShipmentOrderLegRunForPlanEntity | undefined,
): string {
  if (!legRun) return '—';
  return rawName(side, legRun);
}

/**
 * 地点キーに使う座標の桁数。6 桁 ≒ 10cm で、同一拠点を指す座標の差はここに収まる。
 *
 * Why 丸める: 同じ場所でも leg_run ごとに精度違いの座標が保存されている
 * （logposeTech/log_web_issues#314。降地の経度が 139.738409 と 139.7384086 の 2 種類）。
 * shipment_order_waypoints は伝票ごとのレコードで緯度経度は string カラムのため、
 * 取り込み時期の違いがそのまま桁数の違いになる。
 *
 * Why not 走行距離 0 でまとめる: 固定ルートの対応づけ
 * (planning_api の apply_to_existing_planning_service.rb) は別店舗でも一律
 * driving_distance_mm: 0 で operation を合成し、時刻も同じ base_time から導出するため
 * arrival/departure が完全一致しうる。走行距離だけを判定材料にすると、別店舗が
 * 先頭店舗に吸収されて画面から消える退行（commit a2fa984b で修正済み）が再発する。
 */
const COORD_KEY_DIGITS = 6;
const COORD_KEY_SCALE = 10 ** COORD_KEY_DIGITS;

/** 末尾 0 を落とした丸め値。`35.700000` でなく `35.7` を返し、従来のキーの形を保つ */
const roundCoord = (value: number): number => Math.round(value * COORD_KEY_SCALE) / COORD_KEY_SCALE;

/**
 * 座標だけの地点キー。V3Plans と同じく lat-lng ベースで、座標が無ければ地点名で代用する。
 * カードの同一性は placeKeyOf（設定に従う）なので、こちらを直接使うのは
 * 「同じ場所での作業をまとめたい」処理だけ（楽観更新の arrival/departure の算出）。
 */
export function coordKeyOf(
  side: PlaceSide,
  legRun: ShipmentOrderLegRunForPlanEntity | undefined,
  fallback: string,
): string {
  if (!legRun) return fallback;
  const lat = side === 'loading' ? legRun.loading_lat : legRun.unloading_lat;
  const lng = side === 'loading' ? legRun.loading_lng : legRun.unloading_lng;
  if (lat != null && lng != null) return `${roundCoord(lat)}-${roundCoord(lng)}`;
  return rawName(side, legRun) || fallback;
}

/**
 * カードの同一性キー。名称でまとめる設定のときだけ座標キーに地点名を足す。
 *
 * Why not 名称だけをキーにする: 同名で住所の違う拠点が合流する。旧地図配車
 * (PlanningsOperationGroup.model.ts:192-230) も「同一座標のグループを名称で分割」で、
 * 名称そのものをキーにはしていない。
 */
export function placeKeyOf(
  side: PlaceSide,
  legRun: ShipmentOrderLegRunForPlanEntity | undefined,
  fallback: string,
  grouping: DeliveryListGroupingValues,
): string {
  const coordKey = coordKeyOf(side, legRun, fallback);
  // legRun が引けなければ名称も引けない。fallback は operation ごとに一意なので、
  // '—' を足しても分け方は変わらず読みにくいだけ
  if (grouping !== 'groupByName' || !legRun) return coordKey;
  return `${coordKey}-${placeNameOf(side, legRun)}`;
}

/**
 * その側の地点が主要倉庫 (places.is_main_warehouse) か。判定は API 側で
 * 名称＋住所の完全一致で行い、ここは結果を読むだけ
 * (logposeTech/log_web_issues#318 の拡張)。
 *
 * Why ここに置く: side を見て legRun の積地／降地フィールドを読む処理はこのファイルに
 * 集めており、カード生成 (adapter.ts) と D&D 直後の楽観更新 (placeLegRunInRow.ts) の
 * 両方がここを import する。片方だけ追随し忘れると、ドロップ直後だけ判定が変わるという
 * 直しにくい挙動になる (placeKeyOf を 1 箇所に集めているのと同じ理由)。
 *
 * フラグが無い (compact 由来・主要倉庫を運用していない会社) ときは false。除外が効かず
 * タグが増える向きに倒れるが、警告を隠す向きには倒れない。
 */
export const isMainWarehouseSide = (
  side: PlaceSide,
  legRun: ShipmentOrderLegRunForPlanEntity | undefined,
): boolean => (
  side === 'loading' ? legRun?.loading_is_main_warehouse : legRun?.unloading_is_main_warehouse
) === true;

/**
 * カードにマージされた legRun のうち 1 件でも主要倉庫なら true。
 *
 * Why not every: 座標だけでキーを作る設定では同一座標に名称違いの legRun がマージされる。
 * 1 件でもマスタの主要倉庫に一致するなら、その座標は拠点そのものなので除外したい。
 */
export const anyMainWarehouse = (
  side: PlaceSide,
  legRuns: readonly ShipmentOrderLegRunForPlanEntity[],
): boolean => legRuns.some((lr) => isMainWarehouseSide(side, lr));
